/**
 * Registrant-facing cohort hub.
 *
 * Route: /cohort/:token
 *
 * Token IS the auth — 32-char hex from registration_tokens.
 * One fetch: GET /api/public/cohort/:token returns everything the page needs.
 * Brand tokens applied to <html> via applyTokensToDom() so every element can
 * use var(--prog-*) custom properties. Google Fonts + favicon swapped on load.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND_NAME, BRAND_HOME_URL, SUPPORT_EMAIL } from './branding';
import BrokenPage from './components/BrokenPage';
import { PolicyLinks } from './components/PolicyLinks';
import { fetchWithRetry } from './lib/fetchRetry';

const API = import.meta.env.VITE_API_BASE_URL as string;

// ────────────────────────────────────────────────────────────────────────────
// Types (mirrors worker endpoint response shape)
// ────────────────────────────────────────────────────────────────────────────

interface SessionAttendee {
  name: string;
  role: string | null;
  photoUrl: string | null;
  tagline: string | null;
  bio: string | null;
}

interface CohortSession {
  id: string;
  title: string;
  description: string | null;
  scheduled_at: string | null;
  duration_minutes: number | null;
  meeting_url: string | null;
  recording_url: string | null;
  status: string;
  sort_order: number;
  attendees: SessionAttendee[];
}

interface TeamMember {
  name: string;
  role: string | null;
  photoUrl: string | null;
  tagline: string | null;
  bio: string | null;
  bookingUrl: string | null;
}

interface BrandInfo {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  palette: Record<string, string>;
  fonts: Record<string, unknown>;
  design_tokens: Record<string, unknown> | null;
  logoUrl: string | null;
  logoWhiteUrl: string | null;
  faviconUrl: string | null;
  logoLinkUrl: string | null;
}

interface ResolvedTokens {
  palette: Record<string, string> | null;
  fonts: Record<string, unknown> | null;
  designTokens: Record<string, unknown> | null;
  designTreatments: { hero?: string; ctaOnLight?: string } | null;
}

interface CohortHubResponse {
  registration: { firstName: string | null; email: string; tier: string | null; status: string | null };
  program: { slug: string; name: string; type: string };
  cohort: { id: string; cohort_name: string; status: string; cohort_starts: string | null; cohort_ends: string | null };
  sessions: CohortSession[];
  team: TeamMember[];
  brand: BrandInfo | null;
  tokens: ResolvedTokens;
  googleFontsUrl: string | null;
  icsUrl: string;
  cohortHubUrl: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Token helpers (same pattern as LetsTalkPage)
// ────────────────────────────────────────────────────────────────────────────

const CSS_PROP_CLEANUP: string[] = [];

function applyTokensToDom(data: CohortHubResponse) {
  const root = document.documentElement;
  const { palette, fonts, designTokens } = data.tokens;

  const set = (prop: string, val: string | null | undefined) => {
    if (val) {
      root.style.setProperty(prop, val);
      CSS_PROP_CLEANUP.push(prop);
    }
  };

  if (palette) {
    set('--prog-text-dark', palette.textDark);
    set('--prog-deep', palette.deep ?? palette.textDark);
    set('--prog-primary', palette.primary);
    set('--prog-bright', palette.bright ?? palette.primary);
    set('--prog-tint', palette.tint);
    set('--prog-canvas', palette.canvas);
    set('--prog-highlight', palette.highlight ?? null);
  }

  const bpal = data.brand?.palette;
  if (bpal) {
    set('--brand-primary', bpal.primary);
    set('--brand-cta', bpal.cta);
    set('--brand-canvas', bpal.canvas);
  }

  const heading = (fonts as any)?.heading as { family?: string } | undefined;
  const body    = (fonts as any)?.body    as { family?: string } | undefined;
  const display = (fonts as any)?.display as { family?: string } | undefined;
  set('--font-heading', heading?.family ? `'${heading.family}', sans-serif` : null);
  set('--font-body',    body?.family    ? `'${body.family}', sans-serif`    : null);
  set('--font-display', display?.family ? `'${display.family}', serif`      : null);

  const radius = (designTokens as any)?.radius as Record<string, number> | undefined;
  const page   = (designTokens as any)?.page   as { maxWidth?: number } | undefined;
  if (radius?.button   != null) set('--radius-button',    `${radius.button}px`);
  if (radius?.card     != null) set('--radius-card',      `${radius.card}px`);
  if (radius?.container != null) set('--radius-container', `${radius.container}px`);
  if (page?.maxWidth   != null) set('--page-max-width',   `${page.maxWidth}px`);
}

function cleanupTokensFromDom() {
  const root = document.documentElement;
  while (CSS_PROP_CLEANUP.length) root.style.removeProperty(CSS_PROP_CLEANUP.pop()!);
}

// ────────────────────────────────────────────────────────────────────────────
// Date formatting
// ────────────────────────────────────────────────────────────────────────────

function formatDateLong(iso: string) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto',
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  }).format(d);
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(d);
}

function formatDateShort(iso: string) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto',
    month: 'short', day: 'numeric',
  }).format(d);
}

function formatDayOfWeek(iso: string) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Toronto', weekday: 'long',
  }).format(new Date(iso));
}

function formatCohortDateRange(starts: string | null, ends: string | null): string {
  if (!starts && !ends) return '';
  if (starts && !ends) return `Starts ${formatDateShort(starts)}`;
  if (!starts && ends) return `Ends ${formatDateShort(ends)}`;
  return `${formatDateShort(starts!)} – ${formatDateShort(ends!)}`;
}

function isInFuture(iso: string): boolean {
  return new Date(iso).getTime() > Date.now();
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function CohortPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'ready' | 'not-found' | 'error'>('loading');
  const [data, setData] = useState<CohortHubResponse | null>(null);

  // 1. Fetch cohort hub data
  useEffect(() => {
    if (!token) { setStatus('not-found'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithRetry(`${API}/api/public/cohort/${encodeURIComponent(token)}`);
        if (!res.ok) {
          if (!cancelled) setStatus(res.status === 404 ? 'not-found' : 'error');
          return;
        }
        const json = await res.json() as CohortHubResponse;
        if (!cancelled) { setData(json); setStatus('ready'); }
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // 2. Apply brand tokens to <html>
  useEffect(() => {
    if (!data) return;
    applyTokensToDom(data);
    return cleanupTokensFromDom;
  }, [data]);

  // 3. Google Fonts
  useEffect(() => {
    if (!data?.googleFontsUrl) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = data.googleFontsUrl;
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [data?.googleFontsUrl]);

  // 4. Document title
  useEffect(() => {
    if (data?.program?.name) {
      document.title = `${data.program.name} — Your cohort hub · ${BRAND_NAME}`;
    }
    return () => { document.title = BRAND_NAME; };
  }, [data?.program?.name]);

  // 5. Brand favicon
  useEffect(() => {
    const faviconUrl = data?.brand?.faviconUrl;
    if (!faviconUrl) return;
    const head = document.head;
    let icon = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const previousHref = icon?.href ?? null;
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      head.appendChild(icon);
    }
    icon.href = faviconUrl;
    return () => {
      const current = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (current) current.href = previousHref ?? '/favicon.svg';
    };
  }, [data?.brand?.faviconUrl]);

  // ── Render states
  if (status === 'loading') return <Shell><LoadingSpinner /></Shell>;
  if (status === 'not-found') return (
    <BrokenPage
      variant="404"
      message="This cohort hub link isn't valid. If you have a registration email from us, the link inside should still work — paste it here or tell us and we'll resend it."
    />
  );
  if (status === 'error') return <BrokenPage variant="error" />;

  const d = data!;
  const firstName = d.registration.firstName ?? 'there';
  const programName = d.program.name;
  const cohortDateRange = formatCohortDateRange(d.cohort.cohort_starts, d.cohort.cohort_ends);

  // First scheduled future session
  const nextSession = d.sessions
    .filter((s) => s.status === 'scheduled' && s.scheduled_at && isInFuture(s.scheduled_at))
    .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())[0] ?? null;

  const logoLinkUrl = d.brand?.logoLinkUrl ?? BRAND_HOME_URL;

  return (
    <div style={pageWrapStyle} data-cohort-hub>
      {/* ── TopBar ── */}
      <div style={topBarStyle}>
        <div style={topBarInnerStyle}>
          <a href={logoLinkUrl} style={wordmarkStyle} aria-label={BRAND_NAME}>
            {d.brand?.logoWhiteUrl
              ? <img src={d.brand.logoWhiteUrl} alt={BRAND_NAME} style={logoImgStyle} />
              : d.brand?.logoUrl
                ? <img src={d.brand.logoUrl} alt={BRAND_NAME} style={logoImgStyle} />
                : <span style={wordmarkTextStyle}>{BRAND_NAME}</span>
            }
          </a>
        </div>
      </div>

      {/* ── Hero ── */}
      <section style={heroStyle}>
        <div style={heroInnerStyle}>
          <h1 style={heroH1Style}>You're in, {firstName}.</h1>
          <p style={heroSubStyle}>{programName}</p>
          <div style={heroPillsStyle}>
            {cohortDateRange && <span style={heroPillStyle}>{cohortDateRange}</span>}
            {d.registration.status && (
              <span style={{ ...heroPillStyle, ...activePillStyle }}>Active</span>
            )}
            {d.cohort.status && d.cohort.status !== 'running' && (
              <span style={heroPillStyle}>{d.cohort.status.charAt(0).toUpperCase() + d.cohort.status.slice(1)}</span>
            )}
          </div>
        </div>
      </section>

      <main style={mainStyle}>

        {/* ── Next session callout ── */}
        {nextSession && (
          <section style={nextSessionCardStyle}>
            <div style={nextSessionLabelStyle}>Next session</div>
            <h2 style={nextSessionTitleStyle}>{nextSession.title}</h2>
            {nextSession.scheduled_at && (
              <p style={nextSessionDateStyle}>
                {formatDayOfWeek(nextSession.scheduled_at)}, {formatDateLong(nextSession.scheduled_at)} · {formatTime(nextSession.scheduled_at)}
              </p>
            )}
            <div style={nextSessionActionsStyle}>
              {nextSession.meeting_url && (
                <a href={nextSession.meeting_url} target="_blank" rel="noopener noreferrer" style={joinBtnStyle}>
                  Join session →
                </a>
              )}
              <a href={d.icsUrl} download={`${programName}.ics`} style={calBtnStyle}>
                Add to calendar
              </a>
            </div>
          </section>
        )}

        {/* ── Your team ── */}
        {d.team.length > 0 && (
          <section style={sectionStyle}>
            <h2 style={sectionHeadingStyle}>Your team</h2>
            <div style={teamGridStyle}>
              {d.team.map((member, i) => (
                <div key={i} style={teamCardStyle}>
                  {member.photoUrl
                    ? <img src={member.photoUrl} alt={member.name} style={teamPhotoStyle} />
                    : <div style={teamPhotoPlaceholderStyle}>{member.name.charAt(0)}</div>
                  }
                  <p style={teamNameStyle}>{member.name}</p>
                  {member.role && (
                    <p style={teamRoleStyle}>
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </p>
                  )}
                  {member.tagline && <p style={teamTaglineStyle}>{member.tagline}</p>}
                  {member.bookingUrl && (
                    <a href={member.bookingUrl} target="_blank" rel="noopener noreferrer" style={bookBtnStyle}>
                      Book a call
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Full schedule ── */}
        {d.sessions.length > 0 && (
          <section style={sectionStyle}>
            <h2 style={sectionHeadingStyle}>Full schedule</h2>
            <div style={scheduleListStyle}>
              {d.sessions.map((s, i) => {
                const isCancelled = s.status === 'cancelled';
                const isCompleted = s.status === 'completed';
                const primaryAttendee = s.attendees[0] ?? null;
                return (
                  <div key={s.id} style={{ ...scheduleRowStyle, ...(isCancelled ? cancelledRowStyle : {}) }}>
                    <div style={sessionNumStyle}>{i + 1}</div>
                    <div style={sessionInfoStyle}>
                      <div style={{ ...sessionTitleStyle, ...(isCancelled ? strikeStyle : {}) }}>
                        {s.title}
                      </div>
                      {s.scheduled_at && (
                        <div style={sessionDateStyle}>
                          {formatDateLong(s.scheduled_at)} · {formatTime(s.scheduled_at)}
                          {s.duration_minutes && ` · ${s.duration_minutes} min`}
                        </div>
                      )}
                      {primaryAttendee && (
                        <div style={sessionHostStyle}>with {primaryAttendee.name}</div>
                      )}
                    </div>
                    <div style={sessionActionsStyle}>
                      {isCancelled && <span style={cancelledBadgeStyle}>Cancelled</span>}
                      {isCompleted && s.recording_url && (
                        <a href={s.recording_url} target="_blank" rel="noopener noreferrer" style={recordingLinkStyle}>
                          Watch recording →
                        </a>
                      )}
                      {isCompleted && !s.recording_url && (
                        <span style={completedBadgeStyle}>Completed</span>
                      )}
                      {!isCancelled && !isCompleted && s.meeting_url && (
                        <a href={s.meeting_url} target="_blank" rel="noopener noreferrer" style={meetingLinkStyle}>
                          Join →
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>

      {/* ── Footer ── */}
      <footer style={footerStyle}>
        <a href={BRAND_HOME_URL} style={footLinkStyle}>{BRAND_NAME}</a>
        {' · '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={footLinkStyle}>{SUPPORT_EMAIL}</a>
        <br />
        <span style={footNoteStyle}>Bookmark this page — registered as {d.registration.email}</span>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={pageWrapStyle} data-cohort-hub>
      <div style={topBarStyle}><div style={topBarInnerStyle}><span style={wordmarkTextStyle}>{BRAND_NAME}</span></div></div>
      {children}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={loadingWrapStyle}>
      <div style={spinnerStyle} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────────────────────

const v = (prop: string, fallback: string) =>
  `var(${prop}, ${fallback})` as React.CSSProperties[keyof React.CSSProperties];

const pageWrapStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: v('--prog-canvas', '#f9fafb') as string,
  fontFamily: v('--font-body', "'Inter', sans-serif") as string,
  color: v('--prog-text-dark', '#111827') as string,
};

const topBarStyle: React.CSSProperties = {
  background: v('--prog-text-dark', '#111827') as string,
  padding: '14px 24px',
};

const topBarInnerStyle: React.CSSProperties = {
  maxWidth: v('--page-max-width', '860px') as string,
  margin: '0 auto',
};

const wordmarkStyle: React.CSSProperties = {
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
};

const logoImgStyle: React.CSSProperties = {
  height: 32,
  display: 'block',
};

const wordmarkTextStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: '#ffffff',
  letterSpacing: '-0.01em',
  fontFamily: v('--font-heading', "'Inter', sans-serif") as string,
};

// ── Hero
const heroStyle: React.CSSProperties = {
  background: `linear-gradient(135deg, ${v('--prog-deep', '#003D32') as string} 0%, ${v('--prog-text-dark', '#002B22') as string} 100%)`,
  padding: '56px 24px 48px',
  color: '#ffffff',
};

const heroInnerStyle: React.CSSProperties = {
  maxWidth: v('--page-max-width', '860px') as string,
  margin: '0 auto',
};

const heroH1Style: React.CSSProperties = {
  margin: '0 0 8px',
  fontSize: 'clamp(28px, 5vw, 42px)',
  fontWeight: 800,
  lineHeight: 1.15,
  letterSpacing: '-0.025em',
  fontFamily: v('--font-heading', "'Inter', sans-serif") as string,
};

const heroSubStyle: React.CSSProperties = {
  margin: '0 0 20px',
  fontSize: 17,
  opacity: 0.82,
  lineHeight: 1.5,
};

const heroPillsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
};

const heroPillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.22)',
  borderRadius: 100,
  padding: '5px 13px',
  fontSize: 12,
  fontWeight: 600,
  color: '#ffffff',
  letterSpacing: '0.02em',
};

const activePillStyle: React.CSSProperties = {
  background: v('--prog-primary', '#00C896') as string,
  border: 'none',
};

// ── Main
const mainStyle: React.CSSProperties = {
  maxWidth: v('--page-max-width', '860px') as string,
  margin: '0 auto',
  padding: '40px 24px 64px',
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
};

// ── Next session
const nextSessionCardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: v('--radius-card', '18px') as string,
  padding: '28px 32px',
  marginBottom: 40,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)',
  borderLeft: `4px solid ${v('--prog-primary', '#00C896') as string}`,
};

const nextSessionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: v('--prog-primary', '#00C896') as string,
  marginBottom: 8,
};

const nextSessionTitleStyle: React.CSSProperties = {
  margin: '0 0 6px',
  fontSize: 20,
  fontWeight: 700,
  color: v('--prog-text-dark', '#111827') as string,
  fontFamily: v('--font-heading', "'Inter', sans-serif") as string,
};

const nextSessionDateStyle: React.CSSProperties = {
  margin: '0 0 20px',
  fontSize: 14,
  color: '#4B5563',
  lineHeight: 1.5,
};

const nextSessionActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
};

const joinBtnStyle: React.CSSProperties = {
  display: 'inline-block',
  background: v('--prog-primary', '#00C896') as string,
  color: '#ffffff',
  padding: '10px 22px',
  borderRadius: v('--radius-button', '8px') as string,
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 700,
  fontFamily: v('--font-heading', "'Inter', sans-serif") as string,
};

const calBtnStyle: React.CSSProperties = {
  display: 'inline-block',
  background: '#F3F4F6',
  color: '#374151',
  padding: '10px 22px',
  borderRadius: v('--radius-button', '8px') as string,
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 600,
};

// ── Section
const sectionStyle: React.CSSProperties = {
  marginBottom: 48,
};

const sectionHeadingStyle: React.CSSProperties = {
  margin: '0 0 20px',
  fontSize: 18,
  fontWeight: 700,
  color: v('--prog-text-dark', '#111827') as string,
  fontFamily: v('--font-heading', "'Inter', sans-serif") as string,
  letterSpacing: '-0.01em',
};

// ── Team
const teamGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: 16,
};

const teamCardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: v('--radius-card', '18px') as string,
  padding: '24px 20px',
  textAlign: 'center',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
};

const teamPhotoStyle: React.CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
  margin: '0 auto 12px',
  border: `3px solid ${v('--prog-tint', '#E6F9F4') as string}`,
};

const teamPhotoPlaceholderStyle: React.CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: v('--prog-tint', '#E6F9F4') as string,
  color: v('--prog-primary', '#00C896') as string,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 26,
  fontWeight: 700,
  margin: '0 auto 12px',
};

const teamNameStyle: React.CSSProperties = {
  margin: '0 0 3px',
  fontSize: 15,
  fontWeight: 700,
  color: v('--prog-text-dark', '#111827') as string,
  fontFamily: v('--font-heading', "'Inter', sans-serif") as string,
};

const teamRoleStyle: React.CSSProperties = {
  margin: '0 0 6px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: v('--prog-primary', '#00C896') as string,
};

const teamTaglineStyle: React.CSSProperties = {
  margin: '0 0 14px',
  fontSize: 12.5,
  color: '#6B7280',
  lineHeight: 1.5,
};

const bookBtnStyle: React.CSSProperties = {
  display: 'inline-block',
  background: v('--prog-tint', '#E6F9F4') as string,
  color: v('--prog-primary', '#00C896') as string,
  padding: '7px 18px',
  borderRadius: v('--radius-button', '8px') as string,
  textDecoration: 'none',
  fontSize: 12.5,
  fontWeight: 700,
  letterSpacing: '-0.01em',
};

// ── Schedule
const scheduleListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  background: '#ffffff',
  borderRadius: v('--radius-card', '18px') as string,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
};

const scheduleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  padding: '18px 24px',
  borderBottom: '1px solid #F3F4F6',
};

const cancelledRowStyle: React.CSSProperties = {
  opacity: 0.5,
};

const sessionNumStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: v('--prog-tint', '#E6F9F4') as string,
  color: v('--prog-primary', '#00C896') as string,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
  marginTop: 2,
};

const sessionInfoStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const sessionTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: v('--prog-text-dark', '#111827') as string,
  marginBottom: 3,
};

const strikeStyle: React.CSSProperties = {
  textDecoration: 'line-through',
};

const sessionDateStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: '#6B7280',
  lineHeight: 1.5,
  marginBottom: 2,
};

const sessionHostStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#9CA3AF',
  fontStyle: 'italic',
};

const sessionActionsStyle: React.CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
};

const meetingLinkStyle: React.CSSProperties = {
  color: v('--prog-primary', '#00C896') as string,
  fontSize: 13,
  fontWeight: 700,
  textDecoration: 'none',
};

const recordingLinkStyle: React.CSSProperties = {
  color: v('--prog-primary', '#00C896') as string,
  fontSize: 13,
  fontWeight: 700,
  textDecoration: 'none',
};

const cancelledBadgeStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#9CA3AF',
  background: '#F3F4F6',
  padding: '3px 9px',
  borderRadius: 100,
};

const completedBadgeStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#6B7280',
  background: '#F3F4F6',
  padding: '3px 9px',
  borderRadius: 100,
};

// ── Loading / error
const loadingWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
};

const spinnerStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: `3px solid ${v('--prog-tint', '#E6F9F4') as string}`,
  borderTopColor: v('--prog-primary', '#00C896') as string,
  animation: 'spin 0.8s linear infinite',
};

// ── Footer
const footerStyle: React.CSSProperties = {
  padding: '20px 24px',
  textAlign: 'center',
  fontSize: 12,
  color: '#9CA3AF',
  borderTop: '1px solid rgba(0,0,0,0.06)',
  lineHeight: 1.8,
};

const footLinkStyle: React.CSSProperties = {
  color: '#9CA3AF',
  textDecoration: 'none',
};

const footNoteStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#C4CAD4',
};
